import { useState } from 'react';
import { messageParts, type ConversationLocations } from '@scoutaround/script-breakdown-shared';

import type { ProjectPerson } from '../../lib/api.js';
import { personName } from './hooks.js';
import { PlacePreview, ScriptLocationPreview, shortPlaceName } from './LocationPreview.js';

const CHIP_STYLE = {
  display: 'inline',
  padding: '0 4px',
  margin: 0,
  border: 'none',
  background: 'var(--color-neutral-100)',
  color: 'var(--ink)',
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
  textDecorationColor: 'var(--color-neutral-400)',
  textUnderlineOffset: 2,
} as const;

/**
 * A message as text: mentions become names, `<#place:…>` and
 * `<#requirement:…>` become chips naming the place or script location as THIS
 * reader may see it, and nothing is ever interpreted as markup (React escapes
 * every text node). Line breaks are kept.
 *
 * A chip opens the place's preview under the message — photograph, where it is,
 * what it is to the production, and the links out. A token the reader cannot
 * resolve (no read of that feature, or a script location since removed) is a
 * plain "a place" / "a script location": the body never carries the name, so
 * nothing is withheld by hiding it.
 */
export function MessageBody({
  body,
  people,
  me,
  projectId,
  locations,
  testId = 'conversation-message-body',
}: {
  body: string;
  people: readonly ProjectPerson[] | undefined;
  me: number | null;
  projectId?: string;
  locations?: ConversationLocations;
  testId?: string;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const parts = messageParts(body);

  const placeOf = (id: number) => locations?.places?.find((p) => p.locationId === id) ?? null;
  const scriptLocationOf = (id: string) =>
    locations?.scriptLocations?.find((s) => s.requirementId === id) ?? null;

  const openPlace = open?.startsWith('place:') ? placeOf(Number(open.slice(6))) : null;
  const openScriptLocation = open?.startsWith('requirement:') ? scriptLocationOf(open.slice(12)) : null;

  const toggle = (key: string) => {
    setOpen((current) => (current === key ? null : key));
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <p style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.55 }}>
        {parts.map((part, i) => {
          if (part.kind === 'text') return <span key={i}>{part.text}</span>;
          if (part.kind === 'mention') {
            return (
              <strong key={i} data-testid="conversation-mention" style={{ color: 'var(--color-accent-700)' }}>
                @{personName(people, part.userId, me)}
              </strong>
            );
          }
          if (part.kind === 'place') {
            const place = placeOf(part.locationId);
            const key = `place:${String(part.locationId)}`;
            return place && projectId ? (
              <button
                key={i}
                type="button"
                data-testid={`${testId}-place-${String(part.locationId)}`}
                aria-expanded={open === key}
                title={place.title}
                onClick={() => {
                  toggle(key);
                }}
                style={CHIP_STYLE}
              >
                #{shortPlaceName(place.title)}
              </button>
            ) : (
              <span key={i} data-testid={`${testId}-place-unknown`} className="mm">
                a place
              </span>
            );
          }
          const scriptLocation = scriptLocationOf(part.requirementId);
          const key = `requirement:${part.requirementId}`;
          return scriptLocation && projectId ? (
            <button
              key={i}
              type="button"
              data-testid={`${testId}-requirement-${part.requirementId}`}
              aria-expanded={open === key}
              title={scriptLocation.title}
              onClick={() => {
                toggle(key);
              }}
              style={CHIP_STYLE}
            >
              #{scriptLocation.title}
            </button>
          ) : (
            <span key={i} data-testid={`${testId}-requirement-unknown`} className="mm">
              a script location
            </span>
          );
        })}
      </p>
      {openPlace && projectId ? (
        <PlacePreview projectId={projectId} place={openPlace} testId={`${testId}-place-preview`} />
      ) : null}
      {openScriptLocation && projectId ? (
        <ScriptLocationPreview
          projectId={projectId}
          scriptLocation={openScriptLocation}
          places={locations?.places ?? null}
          testId={`${testId}-requirement-preview`}
        />
      ) : null}
    </div>
  );
}
